import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { FexidocLogo } from "@/components/FexidocLogo";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * Handles the Emergent Google auth return. The provider drops the user back on
 * whatever route we asked for with #session_id=... in the fragment.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams((location.hash || "").replace(/^#/, "")).get("session_id");
    // Where the user was heading before we hijacked the render (keeps invite links intact)
    const target = window.location.pathname + window.location.search;
    (async () => {
      if (!sessionId) { setError("This sign-in link is missing its session."); return; }
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setToken(data.token);
        window.history.replaceState({}, "", target);
        await refresh();
        nav(target === "/" || target.startsWith("/login") ? "/app" : target, { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "We could not complete your Google sign-in.");
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="auth-callback">
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center mb-8"><FexidocLogo size={30} /></div>
        {error ? (
          <div className="fx-panel p-8">
            <AlertTriangle size={26} className="mx-auto text-amber-400" />
            <div className="mt-4 font-display text-lg font-semibold text-white">Sign-in failed</div>
            <div className="text-sm text-slate-400 mt-1" data-testid="auth-callback-error">{error}</div>
            <button onClick={() => nav("/login", { replace: true })} className="fx-btn-primary text-white text-sm px-4 py-2 rounded-lg mt-5" data-testid="auth-callback-retry">Back to sign in</button>
          </div>
        ) : (
          <div className="text-slate-400 text-sm inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin text-cyan-300" /> Signing you in…
          </div>
        )}
      </div>
    </div>
  );
}
